<template>
  <table v-if="orderType === 1" class="finished-order-table">
    <thead>
      <tr>
        <th class="stock-info">
          {{ $t('trade.order-table.name-symbol') }}
        </th>
        <th class="action">
          {{ $t('trade.order-table.direction') }}
        </th>
        <th class="price">
          {{ $t('trade.order-table.amount-price') }}
        </th>
        <th class="date">
          {{ $t('trade.order-table.finish-time') }}
        </th>
      </tr>
    </thead>
    <tbody>
      <template v-for="order in normalizedOrders">
        <tr
          :key="order.uuid"
          :class="{ selected: order.uuid === activeId }"
          @click.prevent.stop="setActiveId(order.uuid)"
        >
          <td class="stock-info">
            <div class="stock-info-container">
              <div :title="stockSymbol(order)" class="icon-name-wrap">
                <MarketMark :market="order.market" />
                <span>{{ stockSymbol(order) }}</span>
              </div>
              <StockNameWithMark :order="order" />
            </div>
          </td>
          <td class="action">
            <OrderActionName :action="order.action" />
          </td>
          <td class="price amount-price">
            <p>{{ order.filledQuantity || order.totalQuantity }}</p>
            <p>
              {{
                (order.avgFillPrice || order.limitPrice || order.latestPrice)
                  | floatNum(order)
              }}
            </p>
          </td>
          <td class="date">
            <p>{{ order.latestTime | formatDate(3) }}</p>
            <p>{{ order.latestTime | formatDate(4) }}</p>
          </td>
          <td class="operation-toggle">
            <span v-if="order.uuid === activeId">
              <Icon name="arrowhead-up" />
            </span>
            <span v-else>
              <Icon name="arrowhead-down" />
            </span>
          </td>
        </tr>
        <operation
          v-if="order.uuid === activeId"
          :key="`${order.uuid}-operation`"
          :order-type="orderType"
          :order="order"
          @open="openOrder"
        />
      </template>
    </tbody>
  </table>

  <table v-else class="open-cancel-order-table">
    <thead>
      <tr>
        <th class="stock-info">
          {{ $t('trade.order-table.name-symbol') }}
        </th>
        <th class="price-type">
          {{ $t('trade.order-table.price-offer') }}
        </th>
        <th class="quantity">
          {{ $t('trade.order-table.total-filled') }}
        </th>
        <th class="action-status">
          {{ $t('trade.order-table.direction-status') }}
        </th>
      </tr>
    </thead>
    <tbody>
      <template v-for="order in normalizedOrders">
        <tr
          :key="order.uuid"
          :class="{ selected: order.uuid === activeId }"
          @click.prevent.stop="setActiveId(order.uuid)"
        >
          <td class="stock-info" :class="{ 'no-price': !isSTPOrLMT(order) }">
            <div class="stock-info-line1">
              <div :title="stockSymbol(order)" class="icon-name-wrap">
                <MarketMark :market="order.market" />
                <span>{{ stockSymbol(order) }}</span>
              </div>
              <OrderTypeName
                v-if="isSTPOrLMT(order)"
                :order-type="order.orderType"
                :is-attach="!!order.parentId"
                :only-trail="true"
                :action="order.action"
              />
              <StockNameWithMark v-else :order="order" />
            </div>
            <div class="stock-info-line2">
              <template v-if="isSTPOrLMT(order)">
                <StockNameWithMark :order="order" />
                <span class="order-type-value">
                  {{ getOrderTypeValue(order) }}
                </span>
              </template>
              <OrderTypeName
                v-else
                :order-type="order.orderType"
                :is-attach="!!order.parentId"
                :only-trail="true"
                :action="order.action"
              />
            </div>
          </td>
          <td class="quantity">
            <p>{{ order.totalQuantity }}</p>
            <p>{{ order.filledQuantity }}</p>
          </td>
          <td class="action-status">
            <OrderActionName :action="order.action" />
            <br />
            <OrderStatusName
              :status="order.status"
              :remark="order.remark || order.messageCode"
            />
          </td>
          <td class="operation-toggle">
            <span v-if="order.uuid === activeId">
              <Icon name="arrowhead-up" />
            </span>
            <span v-else>
              <Icon name="arrowhead-down" />
            </span>
          </td>
        </tr>
        <Operation
          v-if="order.uuid === activeId"
          :key="`${order.uuid}-operation`"
          :order-type="orderType"
          :order="order"
          @open="openOrder"
          @orderCanceled="orderCanceled"
        />
      </template>
    </tbody>
  </table>
</template>
<script>
import MarketMark from 'mod/MarketMark'
import OrderActionName from 'mod/TextFields/OrderActionName'
import OrderTypeName from 'mod/TextFields/OrderTypeName'
import OrderStatusName from 'mod/TextFields/OrderStatusName'
import Icon from 'com/Icon'
import Operation from './Operation'
import StockNameWithMark from './StockNameWithMark'
import choiceFilter from 'utils/choiceFilter'
import { SEC_TYPE } from 'const/stock'
import { ORDER_TYPE } from 'const/trade'
import { mapGetters } from 'vuex'

export default {
  name: 'OrderTable',
  data() {
    return {
      activeId: null
    }
  },
  components: {
    OrderActionName,
    OrderTypeName,
    MarketMark,
    OrderStatusName,
    Icon,
    Operation,
    StockNameWithMark
  },
  props: {
    orders: {
      type: Array,
      required: true
    },
    symbol: String,
    orderType: { required: true }
  },
  computed: {
    ...mapGetters(['isIb']),
    normalizedOrders() {
      return this.orders.map(order => {
        return {
          ...order,
          uuid: order.orderId || order.idStr || order.id,
          digits: choiceFilter(order)
        }
      })
    }
  },
  methods: {
    needShowPrice(order) {
      return (
        order.orderType === ORDER_TYPE.LMT || order.orderType === ORDER_TYPE.STP
      )
    },
    setActiveId(uuid) {
      // 模拟订单没有orderid
      if (this.activeId === uuid) {
        this.activeId = null
      } else {
        this.activeId = uuid
      }
    },
    stockSymbol(order) {
      if (order.secType === SEC_TYPE.OPT) {
        return this.$options.filters.formatOptionName(order, 1)
      }
      return order.symbol
    },
    orderCanceled() {
      this.$emit('orderCanceled')
      this.activeId = null
    },
    openOrder(order) {
      this.$emit('openOrder', order)
    },
    isSTPOrLMT(order) {
      return [ORDER_TYPE.LMT, ORDER_TYPE.STP].includes(order.orderType)
    },
    getOrderTypeValue(order) {
      const floatNum = this.$options.filters.floatNum
      if (order.orderType === ORDER_TYPE.LMT) {
        return floatNum(order.limitPrice, order)
      }
      if (order.orderType === ORDER_TYPE.STP) {
        return floatNum(order.stpPrice, order)
      }
    }
  },
  watch: {
    symbol() {
      this.activeId = null
    }
  }
}
</script>

<style lang="less" scoped src="./styles.less"></style>
